import {
  computed,
  DestroyRef,
  DOCUMENT,
  effect,
  inject,
  Service,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { mapBanners } from '../../data/qr.mapper';
import { QrService } from '../../data/qr.service';
import { QrBanner } from '../../data/qr.types';
import { LanguageService } from '../../core/language.service';
import { QrTokenService } from '../../core/qr-token.service';
import { MenuState } from './menu-state';

/** Баннеры: список — сразу, первая картинка — только после меню; ошибка — просто без блока. */
@Service({ autoProvided: false })
export class BannerStore {
  private qr = inject(QrService);
  private qrToken = inject(QrTokenService);
  private menu = inject(MenuState);
  private lang = inject(LanguageService).lang;
  private doc = inject(DOCUMENT);
  private destroyRef = inject(DestroyRef);

  private readonly raw = signal<(QrBanner | null)[] | null>(null);
  readonly banners = computed(() => mapBanners(this.raw(), this.lang()));

  private readonly _ready = signal(false);
  /** Первая картинка декодирована: блок показывается сразу целым, без пустой рамки. */
  readonly ready = this._ready.asReadonly();
  /** Держит декод живым, пока не появится разметка: иначе GC может его выбросить. */
  private gate: HTMLImageElement | null = null;

  constructor() {
    // Картинку качаем после меню, чтобы на слабом интернете не делить с ним канал.
    effect(() => {
      const first = this.banners()[0];
      if (!first || !this.menu.hasMenu() || untracked(this._ready)) return;
      const img = this.doc.createElement('img');
      img.src = first.imageUrl;
      this.gate = img;
      // Битая картинка тоже открывает блок: её подменит appFallbackImage. Декод картинки
      // прошлого языка (язык сменили, пока качалась) блок не открывает.
      void img
        .decode()
        .catch(() => undefined)
        .then(() => {
          if (this.banners()[0]?.imageUrl === first.imageUrl) this._ready.set(true);
        });
    });
  }

  load(): void {
    if (!this.qrToken.value) return;
    this.qr
      .getBanners()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.raw.set(res),
        error: () => {},
      });
  }
}
